// Indian number formatting: Rs 1,24,500.00
// The dash this app uses everywhere for "there is no value here" -- the same
// glyph MaterialTable's live columns and the picker's price column render.
export const UNSET_DASH = "—";

/** Rupees, or the unset dash when there is no figure at all.
 *
 *  ⚠️ undefined/null AND NaN ARE DIFFERENT PROBLEMS AND ARE TREATED
 *  DIFFERENTLY ON PURPOSE.
 *
 *    undefined / null  ABSENCE BY DESIGN. A product off the real master has
 *                      no price -- anixify.product has no such column -- so a
 *                      line can legitimately have no listPrice and no
 *                      netPrice until someone types one. That renders as the
 *                      dash, never as "Rs 0.00": a zero here would claim the
 *                      part is free, and this codebase's whole live/est
 *                      discipline is that absent is not zero.
 *
 *    NaN               A BUG. Some arithmetic went wrong upstream. It keeps
 *                      rendering as "Rs NaN", loudly, because quietly
 *                      dashing it would hide exactly the defect worth
 *                      finding. Do not "tidy" this into the branch above.
 *
 *  Guarding here rather than at each call site is deliberate: this project
 *  compiles with strictNullChecks OFF, so the compiler flags nothing when an
 *  optional number reaches a `number` parameter. One guard in the formatter
 *  covers every reader, including ones a later change forgets about. */
export function formatRupees(value: number): string {
  if (value === undefined || value === null) return UNSET_DASH;
  const formatted = moneyNumberFormat().format(value);
  return `${moneyFormat.symbol} ${formatted}`;
}

// ---- configurable money format (configurable identity P1-11, 2026-10-29) ----
//
// Settings > Company holds the number format (a locale), the currency symbol
// and the amount-in-words style. ⚠️ THE DEFAULTS ARE TODAY'S VALUES -- en-IN,
// "Rs", Indian (Lakh/Crore) -- and they apply until the settings have been
// read in this tab, so nothing printed changes for a deployment that never
// edits them. The setting changes DISPLAY ONLY: no figure is computed here.
export type AmountWords = "indian" | "international";
let moneyFormat: { locale: string; symbol: string; words: AmountWords } =
  { locale: "en-IN", symbol: "Rs", words: "indian" };
let cachedFormat: { locale: string; nf: Intl.NumberFormat } | null = null;

function moneyNumberFormat(): Intl.NumberFormat {
  if (!cachedFormat || cachedFormat.locale !== moneyFormat.locale) {
    cachedFormat = {
      locale: moneyFormat.locale,
      nf: new Intl.NumberFormat(moneyFormat.locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
    };
  }
  return cachedFormat.nf;
}

/** Apply Settings > Company's money format. Unusable values are ignored and
 *  the previous (by default today's) format stays. */
export function setMoneyFormat(input: {
  locale?: string | null; symbol?: string | null; words?: string | null;
}): void {
  const next = { ...moneyFormat };
  if (input.locale) {
    try {
      if (Intl.NumberFormat.supportedLocalesOf([input.locale]).length) next.locale = input.locale;
    } catch { /* an invalid tag keeps the previous locale */ }
  }
  if (input.symbol && input.symbol.trim()) next.symbol = input.symbol.trim();
  if (input.words === "indian" || input.words === "international") next.words = input.words;
  moneyFormat = next;
}

/** The locale money is grouped in -- for the few places that format a
 *  foreign-currency figure themselves (no symbol, same grouping). */
export function moneyLocale(): string {
  return moneyFormat.locale;
}

/** A whole amount in words, in the configured style. */
export function numberToWords(value: number): string {
  return moneyFormat.words === "international"
    ? numberToWordsInternational(value)
    : numberToWordsIndian(value);
}

export function formatPercent(value: number): string {
  return `${value.toFixed(1)}%`;
}

// e.g. "14 Mar 2026"
export function formatDate(iso: string): string {
  const d = new Date(iso + "T00:00:00");
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const day = String(d.getDate()).padStart(2, "0");
  return `${day} ${months[d.getMonth()]} ${d.getFullYear()}`;
}

// A server TIMESTAMP ("2026-09-24 09:50:12" -- Odoo's Datetime string, UTC)
// as the viewer's own calendar date, formatted like formatDate. formatDate
// takes a DATE only: handed a timestamp it appended "T00:00:00" to it and
// printed "NaN undefined NaN" (the PO documents list, found by the staging
// browser tests 2026-09-24). "" when the value is not a timestamp.
export function formatTimestampDate(ts: string | null | undefined): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2}):(\d{2})/.exec(ts ?? "");
  if (!m) return "";
  const d = new Date(Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], +m[6]));
  const local = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  return formatDate(local);
}

const ONES = [
  "", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine",
  "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen",
  "Seventeen", "Eighteen", "Nineteen",
];
const TENS = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];

function twoDigitsToWords(n: number): string {
  if (n < 20) return ONES[n];
  const tens = Math.floor(n / 10);
  const ones = n % 10;
  return TENS[tens] + (ones ? ` ${ONES[ones]}` : "");
}

function threeDigitsToWords(n: number): string {
  const hundreds = Math.floor(n / 100);
  const rest = n % 100;
  const parts: string[] = [];
  if (hundreds) parts.push(`${ONES[hundreds]} Hundred`);
  if (rest) parts.push(twoDigitsToWords(rest));
  return parts.join(" ");
}

// Indian numbering (Crore/Lakh/Thousand), integer rupees only — e.g.
// 4216000 -> "Forty Two Lakh Sixteen Thousand". Caller adds "Rupees"/"Only".
export function numberToWordsIndian(value: number): string {
  const n = Math.round(Math.abs(value));
  if (n === 0) return "Zero";
  const crore = Math.floor(n / 10000000);
  const lakh = Math.floor((n % 10000000) / 100000);
  const thousand = Math.floor((n % 100000) / 1000);
  const hundred = n % 1000;
  const parts: string[] = [];
  if (crore) parts.push(`${threeDigitsToWords(crore)} Crore`);
  if (lakh) parts.push(`${threeDigitsToWords(lakh)} Lakh`);
  if (thousand) parts.push(`${threeDigitsToWords(thousand)} Thousand`);
  if (hundred) parts.push(threeDigitsToWords(hundred));
  return parts.join(" ");
}

/** Million / Billion grouping, same rules as numberToWordsIndian: whole
 *  units, sign dropped, "Zero" for nothing. */
export function numberToWordsInternational(value: number): string {
  const n = Math.round(Math.abs(value));
  if (n === 0) return "Zero";
  const scales: [number, string][] = [[1e12, "Trillion"], [1e9, "Billion"], [1e6, "Million"], [1e3, "Thousand"]];
  let rest = n;
  const parts: string[] = [];
  for (const [size, name] of scales) {
    const count = Math.floor(rest / size);
    if (count) parts.push(`${threeDigitsToWords(count)} ${name}`);
    rest %= size;
  }
  if (rest) parts.push(threeDigitsToWords(rest));
  return parts.join(" ");
}
